import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { pmGet } from '../api';
import './ExecutiveDashboard.css';

/**
 * EXECUTIVE DASHBOARD — the read-across of every delivery program.
 *
 * NEW WORK, commissioned by the owner (2026-09-30) from an approved prototype. Not a port:
 * the source app has no equivalent screen, so §1.2's fidelity rule does not apply and the
 * design is the one the owner signed off.
 *
 * PROJECT MANAGEMENT DATA ONLY — the owner's ruling. Programs, work items, phase tracking,
 * budget payments, resource effort, execution risks and the action register. Nothing from
 * Inventory Registry: no opportunities, no business case, no ROI. An Inventory Registry
 * dashboard is separate work.
 *
 * Everything the server returns is already scoped: finalized programs only, and filtered to
 * what this viewer may see — so two people comparing totals will legitimately differ.
 */

/* ── Vocabulary ─────────────────────────────────────────────────────────────────────── */

/*
 * EVERY phase, in the module's own order — `catalogs.ts` PHASE_CATALOG.
 *
 * Waterfall programs use the five core phases; an Agile one uses Agile Delivery; anything
 * with no methodology, and every programme-level Global work item, sits in Delivery. A
 * dashboard that listed only the core five would under-report every Agile and every flat
 * program, so all seven are read and the empty ones simply draw nothing.
 */
const PHASES = [
  'Assessment_Planning',
  'Design_Architecture',
  'Development',
  'Testing_Validation',
  'GoLive_Optimization',
  'Agile_Delivery',
  'Flat_Delivery',
] as const;
const PHASE_LABEL = [
  'Assessment & planning',
  'Design & architecture',
  'Development',
  'Testing & validation',
  'Go-live & optimisation',
  'Agile delivery',
  'Delivery',
];
const PHASE_SHORT = ['Assess', 'Design', 'Build', 'Test', 'Go-live', 'Agile', 'Delivery'];
/* The core five share the blue ramp; the two non-waterfall bands get their own hues so a
   mixed portfolio never reads as if an Agile program were a late waterfall one. */
const PHASE_TOK = ['--ex-s1', '--ex-s2', '--ex-s3', '--ex-s4', '--ex-s5', '--ex-agile', '--ex-flat'];
const RISK_SEV = ['Critical', 'High', 'Medium', 'Low'];
const RISK_STATE = ['Identified', 'Mitigating', 'Contained'];
const AGE_BUCKETS: [number, number, string][] = [
  [0, 14, '0–14 days'],
  [15, 30, '15–30'],
  [31, 60, '31–60'],
  [61, 99999, '60+ days'],
];
const PRIORITIES: [string, string][] = [
  ['Critical', '--ex-crit'],
  ['High', '--ex-warn'],
  ['Medium', '--ex-s3'],
  ['Low', '--ex-s1'],
];

/* ── Wire types ─────────────────────────────────────────────────────────────────────── */

interface PoolRow {
  id: string; description: string; methodology: string; origin: string;
  start_date: string | null; budget: number; items_total: number; items_done: number;
  phase_start: string | null; phase_end: string | null; baseline_end: string | null;
  paid: number; critical_risks: number;
}
interface ProgramRow { delivery_project_id: string; description: string; methodology: string; start_date: string | null }
interface PhaseRow {
  delivery_project_id: string; phase: string; total: number; done: number; blocked: number;
  in_progress: number; not_started: number; progress: number; budget_plan: number; budget_actual: number; fte_plan: number; fte_actual: number;
  phase_start: string | null; phase_end: string | null;
  baseline_start: string | null; baseline_end: string | null;
}
interface OverdueRow {
  id: number; delivery_project_id: string; name: string; phase: string; owner: string;
  stream: string; work_item_type: string; status: string; progress: number;
  delivered_by_date: string; days_late: number;
}
interface RiskRow {
  id: number; delivery_project_id: string; title: string; owner: string;
  severity: string; status: string; mitigation: string; due_date: string | null;
}
interface ActionRow {
  id: number; delivery_project_id: string; title: string; owner: string;
  priority: string; status: string; created_date: string | null; due_date: string | null; age_days: number;
}
interface PaymentRow { delivery_project_id: string; phase: string; vendor: string; amount: number; payment_date: string | null }
interface PeopleRow { owner: string; delivery_project_id: string; items: number; open_items: number }
interface DashData {
  programs: ProgramRow[]; phases: PhaseRow[]; overdue: OverdueRow[]; risks: RiskRow[];
  actions: ActionRow[]; payments: PaymentRow[]; people: PeopleRow[];
}

/* ── Small helpers ──────────────────────────────────────────────────────────────────── */

const DAY = 86_400_000;
const money = (v: number) =>
  v >= 1_000_000 ? '$' + (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1) + 'M'
  : v >= 1000 ? '$' + Math.round(v / 1000) + 'k'
  : '$' + Math.round(v);
const pctTxt = (v: number) => Math.round(v) + '%';
const sum = <T,>(a: T[], f: (x: T) => number) => a.reduce((t, x) => t + f(x), 0);
/** Dates from the server are `YYYY-MM-DD`; parse as LOCAL midnight, never UTC. */
const parseDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? new Date(+m[1]!, +m[2]! - 1, +m[3]!) : null;
};
const shortDate = (d: Date | null) =>
  d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
const clip = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + '…' : s);

/** Reads a CSS custom property off the screen root, so charts follow the stylesheet. */
function useTokens(root: HTMLElement | null) {
  return useCallback(
    (name: string) => (root ? getComputedStyle(root).getPropertyValue(name).trim() : ''),
    [root],
  );
}

/* ── Tooltip ────────────────────────────────────────────────────────────────────────── */

interface TipState { html: ReactNode; x: number; y: number }
function useTip() {
  const [tip, setTip] = useState<TipState | null>(null);
  const show = useCallback((e: { clientX: number; clientY: number }, html: ReactNode) => {
    setTip({ html, x: e.clientX, y: e.clientY });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div
      className="pm-exec-tip"
      style={{
        left: Math.min(tip.x + 14, window.innerWidth - 285),
        top: Math.min(tip.y + 14, window.innerHeight - 160),
      }}
    >
      {tip.html}
    </div>
  ) : null;
  return { show, hide, node };
}
const TipRow = ({ k, v }: { k: string; v: ReactNode }) => (
  <div className="r"><span className="k">{k}</span><span>{v}</span></div>
);

/* ── Derived model ──────────────────────────────────────────────────────────────────── */

interface Program {
  id: string; name: string; methodology: string;
  start: Date | null; end: Date | null; baselineEnd: Date | null;
  slipDays: number; budget: number; spent: number;
  items: number; done: number; blocked: number; inProgress: number; notStarted: number;
  progress: number; elapsed: number;
  ftePlan: number; fteActual: number;
  phases: { k: number; total: number; done: number; blocked: number; start: Date | null; end: Date | null;
            baselineEnd: Date | null; budgetPlan: number; budgetActual: number; ftePlan: number; fteActual: number;
            inProgress: number; notStarted: number }[];
  state: 'ok' | 'watch' | 'late';
}

function buildPrograms(data: DashData): Program[] {
  const byId: Record<string, PhaseRow[]> = {};
  data.phases.forEach((r) => (byId[r.delivery_project_id] ??= []).push(r));
  const paid: Record<string, number> = {};
  data.payments.forEach((p) => (paid[p.delivery_project_id] = (paid[p.delivery_project_id] ?? 0) + Number(p.amount || 0)));

  return data.programs.map((p) => {
    const rows = (byId[p.delivery_project_id] ?? []).slice()
      .sort((a, b) => PHASES.indexOf(a.phase as never) - PHASES.indexOf(b.phase as never));
    const phases = PHASES.map((ph, k) => {
      const r = rows.find((x) => x.phase === ph);
      return {
        k,
        total: Number(r?.total ?? 0), done: Number(r?.done ?? 0), blocked: Number(r?.blocked ?? 0),
        inProgress: Number(r?.in_progress ?? 0), notStarted: Number(r?.not_started ?? 0),
        start: parseDate(r?.phase_start), end: parseDate(r?.phase_end),
        baselineEnd: parseDate(r?.baseline_end),
        budgetPlan: Number(r?.budget_plan ?? 0), budgetActual: Number(r?.budget_actual ?? 0),
        ftePlan: Number(r?.fte_plan ?? 0), fteActual: Number(r?.fte_actual ?? 0),
      };
    });
    const items = sum(phases, (x) => x.total);
    const done = sum(phases, (x) => x.done);
    const start = parseDate(p.start_date) ?? phases.map((x) => x.start).find(Boolean) ?? null;
    const ends = phases.map((x) => x.end).filter(Boolean) as Date[];
    const end = ends.length ? new Date(Math.max(...ends.map((d) => d.getTime()))) : null;
    const baselineEnd = parseDate(rows[0]?.baseline_end ?? null);
    const slipDays = end && baselineEnd ? Math.round((end.getTime() - baselineEnd.getTime()) / DAY) : 0;
    const now = Date.now();
    const elapsed = start && end && end.getTime() > start.getTime()
      ? Math.max(0, Math.min(100, ((now - start.getTime()) / (end.getTime() - start.getTime())) * 100))
      : 0;
    const progress = items ? (done / items) * 100 : 0;
    const budget = sum(phases, (x) => x.budgetPlan);
    const actualFromPhases = sum(phases, (x) => x.budgetActual);
    return {
      id: p.delivery_project_id, name: p.description || p.delivery_project_id,
      methodology: p.methodology || '—',
      start, end, baselineEnd, slipDays, budget,
      spent: paid[p.delivery_project_id] ?? actualFromPhases,
      items, done, blocked: sum(phases, (x) => x.blocked),
      inProgress: sum(phases, (x) => x.inProgress), notStarted: sum(phases, (x) => x.notStarted),
      progress, elapsed,
      ftePlan: sum(phases, (x) => x.ftePlan), fteActual: sum(phases, (x) => x.fteActual),
      phases,
      state: slipDays > 22 ? 'late' : slipDays > 7 ? 'watch' : 'ok',
    };
  });
}

/* ── Help ───────────────────────────────────────────────────────────────────────────── */

const EXEC_HELP: HelpContent = {
  title: 'Executive Dashboard',
  icon: '📊',
  purpose:
    'One read across every delivery program you can see — schedule, work closed, budget, ' +
    'effort, risk and open actions — so a steering committee can find the two or three ' +
    'programs that need a decision before it reads any of the detail screens.',
  doHere: [
    'Pick the programs with the <strong>program selector</strong>. Search by id, name or methodology, tick any combination, and remove one with the × on its chip.',
    'Use the <strong>quick filters</strong> — Behind schedule, Overspending, Critical risk, Go-live soon — to select by condition instead of by name.',
    'Read the <strong>headline</strong> first: it names the programs carrying the exposure and recomputes with your selection.',
    'Move between <strong>Overview</strong>, <strong>Schedule</strong>, <strong>Budget</strong>, <strong>Resources</strong> and <strong>Risks &amp; actions</strong>. Hover any bar, circle or cell for its underlying numbers.',
  ],
  fits: 'Status Reporting &rarr; <strong>Executive Dashboard</strong> &rarr; Closeout &amp; Audit',
  firstTime:
    'Press <strong>Behind schedule</strong>, then read the Gantt on Overview. The copper tail on a bar is the slippage against the finalized baseline.',
  sections: [
    {
      label: 'What the numbers are read from',
      type: 'html',
      html:
        'Everything here comes from <strong>Project Management</strong> and nothing else: delivery ' +
        'programs, work items and the deliverable catalogue, phase tracking (dates, budget and FTE ' +
        'plan against actual), budget payments, resource effort, the execution risk register and the ' +
        'action register. <strong>Nothing is read from Opportunity Registry</strong> — no opportunities, ' +
        'no business case, no ROI.<br><br>' +
        'Figures are <strong>per viewer</strong>: you see only the delivery programs you have been ' +
        'given access to, so two people comparing this screen will legitimately see different totals.',
    },
    {
      label: 'How to read the charts',
      type: 'defs',
      items: [
        { term: 'Gantt', color: '#0b5d8f', def: 'One bar per program, split by phase and filled to the share of work items closed. The dashed rule beneath is the finalized baseline; a <strong>copper tail</strong> is slippage past it.' },
        { term: 'Confidence', color: '#8f5f10', def: 'Schedule movement against work closed versus time elapsed. Circle area is budget — the large circles bottom-right are where money and trouble coincide.' },
        { term: 'Utilisation', color: '#1c7a55', def: 'Captured effort as a share of the resource plan, per phase. Amber is work that has not started; red is a phase carrying more than it was planned to.' },
        { term: 'Ageing', color: '#a32a2a', def: 'Open actions by how long they have been raised. Critical and high actions past 30 days are the ones that quietly become issues.' },
      ],
    },
  ],
  links: [
    { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' },
    { label: 'Action Register →', href: '/project_management/program-management/delivery-execution/action-register' },
  ],
};

/* ════════════════════════════════════════════════════════════════════════════════════ */

type View = 'overview' | 'schedule' | 'budget' | 'resource' | 'risk';
type QuickKey = 'behind' | 'overspend' | 'critical' | 'golive';

export function ExecutiveDashboard() {
  /**
   * THE SCREEN IS MEASURED, NOT SIZED IN PERCENT.
   *
   * `height: 100%` does not work in this shell: `<main>` carries `min-height: 100vh` and
   * then grows with its content, so a percentage resolves against nothing and the WINDOW
   * scrolls — taking the heading and the toolbar with it. The height is measured from the
   * viewport instead, re-measured on resize and whenever `<main>` changes, and a CALLBACK
   * ref is used because the effect has to re-run when the node actually mounts.
   *
   * This is CLAUDE.md, "A screen header never scrolls", and the same block ActivityTimeline
   * and Users already carry. Copied rather than reinvented.
   */
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!root) return;
    const main = root.closest('main');
    const measure = () => {
      const top = root.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [root]);
  const tok = useTokens(root);
  const { show, hide, node: tipNode } = useTip();

  const [pool, setPool] = useState<PoolRow[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<View>('overview');

  /* Pool first; then default to the eight largest so the screen opens with something to read. */
  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ success: boolean; programs: PoolRow[]; error?: string }>(
          '/executive-dashboard/programs',
        );
        if (!d.success) { setErr(d.error || 'Could not load programs'); setLoading(false); return; }
        const programs = d.programs ?? [];
        setPool(programs);
        setSelected(programs.slice().sort((a, b) => Number(b.budget) - Number(a.budget)).slice(0, 8).map((p) => p.id));
        if (!programs.length) setLoading(false);
      } catch {
        setErr('Could not load programs'); setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected.length) { setData(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const d = await pmGet<DashData & { success: boolean; error?: string }>(
          `/executive-dashboard/data?ids=${encodeURIComponent(selected.join(','))}`,
        );
        if (cancelled) return;
        if (!d.success) { setErr(d.error || 'Could not load dashboard'); setData(null); }
        else { setErr(null); setData(d); }
      } catch {
        if (!cancelled) { setErr('Could not load dashboard'); setData(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const programs = useMemo(() => (data ? buildPrograms(data) : []), [data]);
  const byId = useMemo(() => Object.fromEntries(programs.map((p) => [p.id, p])), [programs]);

  /**
   * THE QUICK FILTERS ARE SELECTORS, NOT INDICATORS.
   *
   * Each one answers "which programs are in this state?" and re-points the Project field at
   * them. Every test reads the POOL — the finalized list the picker already has — so a
   * filter can carry its own count and say "3" or nothing before anything is loaded, rather
   * than being a button you press to discover it finds nothing.
   */
  const FILTERS: { key: QuickKey; label: string; test: (p: PoolRow) => boolean }[] = useMemo(() => {
    const now = Date.now();
    const slipOf = (p: PoolRow) => {
      const end = parseDate(p.phase_end), base = parseDate(p.baseline_end);
      return end && base ? Math.round((end.getTime() - base.getTime()) / DAY) : 0;
    };
    return [
      { key: 'behind', label: 'Behind schedule', test: (p) => slipOf(p) > 22 },
      { key: 'overspend', label: 'Overspending', test: (p) => Number(p.budget) > 0 && Number(p.paid) > Number(p.budget) * 0.95 },
      { key: 'critical', label: 'Critical risk', test: (p) => Number(p.critical_risks) > 0 },
      { key: 'golive', label: 'Go-live < 90 days', test: (p) => {
          const e = parseDate(p.phase_end);
          return !!e && e.getTime() > now && e.getTime() < now + 90 * DAY;
        } },
    ];
  }, []);

  const quick = (kind: QuickKey) => {
    const f = FILTERS.find((x) => x.key === kind);
    if (!f) return;
    const ids = pool.filter(f.test).map((p) => p.id);
    if (ids.length) setSelected(ids.slice(0, 25));
  };

  /* ── Aggregates ───────────────────────────────────────────────────────────────────── */
  const agg = useMemo(() => {
    const budget = sum(programs, (p) => p.budget);
    const spent = sum(programs, (p) => p.spent);
    const items = sum(programs, (p) => p.items);
    const done = sum(programs, (p) => p.done);
    const ftePlan = sum(programs, (p) => p.ftePlan);
    const fteActual = sum(programs, (p) => p.fteActual);
    const late = programs.filter((p) => p.slipDays > 22).sort((a, b) => b.budget - a.budget);
    const crit = (data?.risks ?? []).filter((r) => r.severity === 'Critical').length;
    const stale = (data?.actions ?? []).filter(
      (a) => a.age_days > 30 && (a.priority === 'Critical' || a.priority === 'High')).length;
    const sev = (n: string) => (data?.risks ?? []).filter((r) => r.severity === n).length;
    const pri = (n: string) => (data?.actions ?? []).filter((a) => a.priority === n).length;
    /* The next phase gates across the whole selection — what a steering committee asks for
       after "how are we doing": what is the next thing that has to land. */
    const gates = programs.flatMap((p) => p.phases
      .filter((f) => f.end && f.end.getTime() > Date.now() && f.total > 0)
      .map((f) => ({ id: p.id, name: p.name, phase: f.k, end: f.end!, done: f.done, total: f.total })))
      .sort((a, b) => a.end.getTime() - b.end.getTime()).slice(0, 5);
    return { budget, spent, items, done, ftePlan, fteActual, late, crit, stale,
             overdue: (data?.overdue ?? []).length,
             onPlan: programs.filter((p) => p.state === 'ok').length,
             watch: programs.filter((p) => p.state === 'watch').length,
             behind: programs.filter((p) => p.state === 'late').length,
             blocked: sum(programs, (p) => p.blocked),
             inProgress: sum(programs, (p) => p.inProgress),
             notStarted: sum(programs, (p) => p.notStarted),
             sev, pri, gates, actions: (data?.actions ?? []).length };
  }, [programs, data]);

  /**
   * The series behind the tile graphics. Each is read off data already loaded — nothing here
   * is modelled, and a tile with nothing to draw simply shows no graphic.
   */
  const spark = useMemo(() => {
    const byPhase = (f: (ph: Program['phases'][number]) => number) =>
      PHASE_SHORT.map((label, k) => ({ label, v: sum(programs, (p) => f(p.phases[k]!)) }));
    /* Payments accumulated by date — the one genuine time series this module keeps. */
    const paid = (data?.payments ?? [])
      .map((x) => ({ d: parseDate(x.payment_date), a: Number(x.amount || 0) }))
      .filter((x): x is { d: Date; a: number } => !!x.d)
      .sort((a, b) => a.d.getTime() - b.d.getTime());
    let acc = 0;
    const paidCum = paid.map((x) => (acc += x.a));
    const od = data?.overdue ?? [];
    return {
      budgetByPhase: byPhase((ph) => ph.budgetPlan),
      closedByPhase: byPhase((ph) => ph.done),
      paidCum,
      overdueByAge: [
        { label: '≤7d', v: od.filter((r) => r.days_late <= 7).length },
        { label: '8–30', v: od.filter((r) => r.days_late > 7 && r.days_late <= 30).length },
        { label: '31–60', v: od.filter((r) => r.days_late > 30 && r.days_late <= 60).length },
        { label: '60+', v: od.filter((r) => r.days_late > 60).length },
      ],
      risksBySeverity: RISK_SEV.map((sv) => ({
        label: sv, v: (data?.risks ?? []).filter((r) => r.severity === sv).length,
      })),
    };
  }, [programs, data]);

  /* ── Render ───────────────────────────────────────────────────────────────────────── */

  const stateCol = (p: Program) =>
    p.state === 'late' ? tok('--ex-crit') : p.state === 'watch' ? tok('--ex-warn') : tok('--ex-good');

  const tabs: [View, string][] = [
    ['overview', 'Overview'], ['schedule', 'Schedule & Gantt'], ['budget', 'Budget'],
    ['resource', 'Resources'], ['risk', 'Risks & actions'],
  ];

  return (
    <div className="pm-exec" ref={setRoot} style={rootH ? { height: rootH } : undefined}>
      <HelpPanel help={withFinalizedRule(EXEC_HELP)} />

      <div className="ex-head">
        <div className="ex-titlerow">
          <i className="material-icons ex-titleicon">query_stats</i>
          <div>
            <h1 className="page-header__title">Executive Dashboard</h1>
            <p className="ex-sub">
              Schedule, budget, effort, risk and open actions across every delivery program you can see.
            </p>
          </div>
          <div className="ex-asof">
            <b>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</b>
            {pool.length} finalized program{pool.length === 1 ? '' : 's'} available
          </div>
        </div>

        <div className="ex-toolbar">
          {/*
            THE PLATFORM'S ONE PROJECT FIELD — `SelectField`, 380px `lg`, bold id then
            description, Ctrl-click to add, never checkboxes. Every other screen in this
            module puts it exactly here, in the strip under the page header, and this screen
            had invented its own picker in a side rail instead.

            `alignItems: 'center'` is spread last, over SelectField's own inline
            `flex-start`, so the label sits level with the control on a single-line toolbar —
            the same override Timeline Finalization needs and for the same reason.
          */}
          <SelectField
            style={{ alignItems: 'center' }}
            label="Project"
            width="lg"
            id="exec-project"
            multiple
            allLabel="All Projects"
            placeholder={PROMPT.project}
            value={selected}
            onChange={(v) => setSelected(v.length ? v : selected)}
            options={pool.map((p) => ({
              value: p.id,
              id: p.id,
              description: p.description,
            }))}
          />

          <div className="ex-chips" style={{ marginLeft: 'auto' }}>
            {FILTERS.map((f) => {
              const hits = pool.filter(f.test).map((p) => p.id);
              const active = hits.length > 0 && hits.length === selected.length
                && hits.every((id) => selected.includes(id));
              return (
                <button
                  key={f.key} type="button"
                  className={`ex-qf${active ? ' on' : ''}`}
                  disabled={!hits.length}
                  aria-pressed={active}
                  title={hits.length
                    ? `Select the ${hits.length} program${hits.length === 1 ? '' : 's'} in this state`
                    : 'No program is in this state'}
                  onClick={() => quick(f.key)}
                >
                  {f.label}<span className="n">{hits.length}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ex-tabs" role="tablist">
          {tabs.map(([k, label]) => (
            <button
              key={k} type="button" role="tab" className="ex-tab" aria-selected={view === k}
              onClick={() => setView(k)}
            >{label}</button>
          ))}
        </div>
      </div>

      <div className="ex-body">
        {err ? (
          <div className="ex-empty"><b>{err}</b>Try again, or pick a different set of programs.</div>
        ) : !pool.length && !loading ? (
          <div className="ex-empty">
            <b>No finalized program to report on</b>
            A program appears here once its timeline has been locked on Project Timeline Finalization.
          </div>
        ) : loading && !programs.length ? (
          <div className="ex-empty">Loading…</div>
        ) : !programs.length ? (
          <div className="ex-empty"><b>Nothing selected</b>Choose one or more programs above.</div>
        ) : (
          <div className="ex-layout">
            <aside className="ex-rail">
            <div className="ex-card">
              <div className="ex-card-hd"><div className="eyebrow">Selection health</div></div>
              <div className="ex-card-bd">
                <div className="ex-gauges">
                  <Gauge value={agg.items ? (agg.done / agg.items) * 100 : 0} label="Progress" tok={tok} />
                  <Gauge value={agg.budget ? (agg.spent / agg.budget) * 100 : 0} label="Budget used" tok={tok} warnOver />
                  <Gauge value={agg.ftePlan ? (agg.fteActual / agg.ftePlan) * 100 : 0} label="Resource util." tok={tok} warnOver />
                </div>
                <div className="ex-ft">
                  <div className="ex-ft-row"><span>Programs</span><b>{programs.length}</b></div>
                  <div className="ex-ft-row"><span>Work items</span><b>{agg.items}</b></div>
                  <div className="ex-ft-row"><span>Planned budget</span><b>{money(agg.budget)}</b></div>
                  <div className="ex-ft-row"><span>Paid to date</span><b>{money(agg.spent)}</b></div>
                  <div className="ex-ft-row"><span>Closed</span><b>{agg.done} / {agg.items}</b></div>
                </div>
              </div>
            </div>

            <div className="ex-card">
              <div className="ex-card-hd"><div className="eyebrow">Schedule state</div></div>
              <div className="ex-card-bd">
                <StackBar
                  parts={[
                    { n: 'On plan', v: agg.onPlan, c: tok('--ex-good') },
                    { n: 'Watch', v: agg.watch, c: tok('--ex-warn') },
                    { n: 'Behind', v: agg.behind, c: tok('--ex-crit') },
                  ]}
                />
              </div>
            </div>

            <div className="ex-card">
              <div className="ex-card-hd">
                <div className="eyebrow">Work items</div>
                <span style={{ font: "500 10px 'IBM Plex Mono',monospace", color: 'var(--ex-muted)' }}>{agg.items}</span>
              </div>
              <div className="ex-card-bd">
                <StackBar
                  parts={[
                    { n: 'Closed', v: agg.done, c: tok('--ex-good') },
                    { n: 'In progress', v: agg.inProgress, c: tok('--ex-accent') },
                    { n: 'Blocked', v: agg.blocked, c: tok('--ex-crit') },
                    { n: 'Not started', v: agg.notStarted, c: tok('--ex-s2') },
                  ]}
                />
                {agg.overdue ? (
                  <div className="ex-ft" style={{ marginTop: 10 }}>
                    <div className="ex-ft-row">
                      <span>Past delivered-by date</span>
                      <b style={{ color: tok('--ex-warn') }}>{agg.overdue}</b>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="ex-card">
              <div className="ex-card-hd"><div className="eyebrow">Risks &amp; actions</div></div>
              <div className="ex-card-bd">
                <StackBar
                  parts={[
                    { n: 'Critical', v: agg.sev('Critical'), c: tok('--ex-crit') },
                    { n: 'High', v: agg.sev('High'), c: tok('--ex-warn') },
                    { n: 'Medium', v: agg.sev('Medium'), c: tok('--ex-s3') },
                    { n: 'Low', v: agg.sev('Low'), c: tok('--ex-s1') },
                  ]}
                  empty="No risk recorded"
                />
                <div className="ex-ft" style={{ marginTop: 10 }}>
                  <div className="ex-ft-row"><span>Open actions</span><b>{agg.actions}</b></div>
                  <div className="ex-ft-row">
                    <span>Open over 30 days</span>
                    <b style={{ color: agg.stale ? tok('--ex-crit') : undefined }}>{agg.stale}</b>
                  </div>
                </div>
              </div>
            </div>

            <div className="ex-card">
              <div className="ex-card-hd"><div className="eyebrow">Next phase gates</div></div>
              <div className="ex-card-bd">
                {agg.gates.length ? (
                  <ul className="ex-gates">
                    {agg.gates.map((g) => (
                      <li key={`${g.id}-${g.phase}`}>
                        <span className="d">{shortDate(g.end)}</span>
                        <span>
                          <span className="t">{PHASE_LABEL[g.phase]}</span>
                          <span className="m">{g.id} · {g.done} of {g.total} closed</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="ex-note" style={{ margin: 0 }}>No phase end is recorded ahead of today.</p>
                )}
              </div>
            </div>
            </aside>

            <div className="ex-main">
            <div className="ex-kpis">
              <Kpi
                label="Planned budget" value={money(agg.budget)}
                sub={`${programs.length} program${programs.length === 1 ? '' : 's'}`}
                graphic={<MiniBars values={spark.budgetByPhase} colour={tok('--ex-accent')} />}
              />
              <Kpi
                label="Paid to date" value={money(agg.spent)}
                delta={agg.budget ? pctTxt((agg.spent / agg.budget) * 100) : '—'}
                deltaCol={agg.spent > agg.budget ? tok('--ex-crit') : tok('--ex-good')}
                sub="of planned budget"
                graphic={spark.paidCum.length > 1
                  ? <SparkArea values={spark.paidCum} colour={tok('--ex-accent')} />
                  : <MiniBars values={spark.budgetByPhase.map((b, i) => ({ label: b.label, v: sum(programs, (p) => p.phases[i]!.budgetActual) }))} colour={tok('--ex-good')} />}
              />
              <Kpi
                label="Work items closed" value={String(agg.done)}
                delta={agg.items ? pctTxt((agg.done / agg.items) * 100) : '—'}
                deltaCol={tok('--ex-good')} sub={`of ${agg.items} in scope`}
                graphic={<MiniBars values={spark.closedByPhase} colour={tok('--ex-good')} />}
              />
              <Kpi
                label="Overdue" value={String(agg.overdue)} delta={agg.overdue ? 'needs triage' : 'clear'}
                deltaCol={agg.overdue ? tok('--ex-warn') : tok('--ex-good')} sub="past delivered-by date"
                graphic={<MiniBars values={spark.overdueByAge} colour={tok('--ex-warn')} />}
              />
              <Kpi
                label="Critical risks" value={String(agg.crit)} delta={agg.crit ? 'open' : 'none'}
                deltaCol={agg.crit ? tok('--ex-crit') : tok('--ex-good')} sub="on the register"
                graphic={<MiniBars values={spark.risksBySeverity} colour={tok('--ex-crit')} />}
              />
            </div>

            {view === 'overview' ? (
              <div className="ex-grid">
                <Panel
                  eyebrow="Timeline" title="Program Gantt against the finalized baseline"
                  note="Each bar is one program split into its delivery phases, filled to the share of work items closed in that phase. The dashed rule beneath is the baseline finish; where a bar runs past it, the copper tail is the slippage. Diamonds are phase gates."
                >
                  <Gantt programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
                <div className="ex-grid ex-g2">
                  <Panel eyebrow="Where to intervene" title="Delivery confidence"
                    note="Schedule movement against work closed versus time elapsed. Circle area is planned budget.">
                    <Confidence programs={programs} tok={tok} show={show} hide={hide} stateCol={stateCol} />
                  </Panel>
                  <Panel eyebrow="Progress" title="Work closed by phase"
                    note="One ring per program, each arc a delivery phase sized by its share of the work items and filled to what is closed.">
                    <Rings programs={programs} tok={tok} show={show} hide={hide} />
                  </Panel>
                </div>
                <Panel eyebrow="Progress detail" title="Completion by phase, program by program"
                  note="The same figures as the rings, read across rather than around — useful when one phase is stalling everywhere.">
                  <PhaseBars programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
                <Panel eyebrow="Watchlist" title="Work items past their delivered-by date"
                  note={`${agg.overdue} open work items are overdue, oldest first.`}>
                  <OverdueTable rows={data?.overdue ?? []} tok={tok} />
                </Panel>
              </div>
            ) : null}

            {view === 'schedule' ? (
              <div className="ex-grid">
                <Panel eyebrow="Phase detail" title="Phase-level Gantt"
                  note="The same programs expanded to one row per phase, with the baseline finish marked.">
                  <PhaseGantt programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
                <Panel eyebrow="Schedule movement" title="Days moved since the finalized baseline"
                  note="Change in forecast finish against the timeline locked on Project Timeline Finalization. Bars to the right are slippage; to the left, recovery.">
                  <Slip programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
              </div>
            ) : null}

            {view === 'budget' ? (
              <div className="ex-grid">
                <div className="ex-grid ex-g2">
                  <Panel eyebrow="Distribution" title="Where the budget sits"
                    note="Every selected program sized by planned budget, with the paid portion saturated.">
                    <Treemap programs={programs} tok={tok} show={show} hide={hide} />
                  </Panel>
                  <Panel eyebrow="Burn" title="Cumulative payments"
                    note="Every payment on the ledger, accumulated by date.">
                    <Burn payments={data?.payments ?? []} budget={agg.budget} tok={tok} show={show} hide={hide} />
                  </Panel>
                </div>
                <Panel eyebrow="Utilisation" title="Spend against planned budget"
                  note="Solid is paid, from the payment ledger; the copper rule is the planned budget from phase tracking. A bar past the rule is an overrun already in the numbers.">
                  <BudgetBullets programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
                <Panel eyebrow="Earned value" title="Cost and schedule performance"
                  note="Value earned against cost incurred, and against work planned. Both read 1.00 on plan; lower-left is spending faster than delivering.">
                  <EarnedValue programs={programs} tok={tok} show={show} hide={hide} stateCol={stateCol} />
                </Panel>
              </div>
            ) : null}

            {view === 'resource' ? (
              <div className="ex-grid">
                <Panel eyebrow="Utilisation" title="Captured effort against the resource plan"
                  note="Per program per phase, captured FTE as a share of planned FTE. Amber is work that has not started; red is a phase carrying more than it was planned to.">
                  <UtilHeat programs={programs} tok={tok} show={show} hide={hide} />
                </Panel>
                <Panel eyebrow="People" title="Who is carrying the open work"
                  note="Named owners on in-scope work items, by how many are still open.">
                  <PeopleBars rows={data?.people ?? []} tok={tok} show={show} hide={hide} />
                </Panel>
              </div>
            ) : null}

            {view === 'risk' ? (
              <div className="ex-grid ex-g2">
                <Panel eyebrow="Risk" title="Open risks by severity and state"
                  note="From the execution risk register. A critical risk still in Identified has nobody working it — those cells are ringed.">
                  <RiskHeat risks={data?.risks ?? []} byId={byId} tok={tok} show={show} hide={hide} />
                </Panel>
                <Panel eyebrow="Action register" title="Open actions by age and priority"
                  note="How long open actions have been sitting since they were raised.">
                  <ActionAge actions={data?.actions ?? []} tok={tok} show={show} hide={hide} />
                </Panel>
              </div>
            ) : null}
            </div>
          </div>
        )}
      </div>
      {tipNode}
    </div>
  );
}

/* ── Presentational pieces ──────────────────────────────────────────────────────────── */

/**
 * The mini graphics in the KPI tiles.
 *
 * The prototype drew invented series here. These are REAL: a cumulative payment curve from
 * the payment ledger, and small distributions — budget by phase, closed work by phase, the
 * overdue items by how late they are, risks by severity. A tile that charted a made-up trend
 * would be the one thing on this screen you could not trust.
 */
function SparkArea({ values, colour }: { values: number[]; colour: string }) {
  if (values.length < 2) return null;
  const W = 124, H = 26;
  const mn = Math.min(...values), mx = Math.max(...values), sp = mx - mn || 1;
  const pts = values.map((v, i) => [
    1 + (i / (values.length - 1)) * (W - 2),
    H - 3 - ((v - mn) / sp) * (H - 8),
  ] as const);
  const d = pts.map((q, i) => `${i ? 'L' : 'M'} ${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1]!;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      <path d={`${d} L ${W - 1} ${H} L 1 ${H} Z`} fill={colour} fillOpacity={0.14} />
      <path d={d} fill="none" stroke={colour} strokeWidth={1.8} strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.6} fill={colour} />
    </svg>
  );
}

function MiniBars({ values, colour }: { values: { label: string; v: number }[]; colour: string }) {
  const live = values.filter((b) => b.v > 0);
  if (!live.length) return null;
  const W = 124, H = 26, gap = 3;
  const bw = (W - gap * (values.length - 1)) / values.length;
  const max = Math.max(...values.map((b) => b.v)) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      {values.map((b, i) => {
        const h = Math.max(b.v > 0 ? 2 : 0, (b.v / max) * (H - 3));
        return (
          <rect key={b.label} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5}
            fill={colour} fillOpacity={b.v > 0 ? 0.9 : 0.18} />
        );
      })}
    </svg>
  );
}

function Kpi({ label, value, delta, deltaCol, sub, graphic }:
  { label: string; value: string; delta?: string; deltaCol?: string; sub: string; graphic?: ReactNode }) {
  return (
    <div className="ex-kpi">
      <div className="eyebrow">{label}</div>
      <div className="v"><b>{value}</b>{delta ? <span className="d" style={{ color: deltaCol }}>{delta}</span> : null}</div>
      {graphic ? <div className="ex-spark">{graphic}</div> : null}
      <small>{sub}</small>
    </div>
  );
}

/**
 * A health ring. `warnOver` marks the two readings where MORE is worse — budget used and
 * resource utilisation both go amber over 85% and red over 100%, while plain progress is
 * simply better the fuller it is.
 */
function Gauge({ value, label, tok, warnOver }:
  { value: number; label: string; tok: (n: string) => string; warnOver?: boolean }) {
  const size = 66, r = size / 2 - 7, c = 2 * Math.PI * r;
  const col = warnOver
    ? value > 100 ? tok('--ex-crit') : value > 85 ? tok('--ex-warn') : tok('--ex-good')
    : tok('--ex-accent');
  return (
    <div className="ex-gauge">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`${label} ${Math.round(value)}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={tok('--ex-line-soft')} strokeWidth={7} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={7} strokeLinecap="round"
          strokeDasharray={`${Math.min(1, value / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle"
          style={{ font: "700 13px 'IBM Plex Mono',monospace", fill: tok('--ex-ink') }}>
          {Math.round(value)}%
        </text>
      </svg>
      <span className="lbl">{label}</span>
    </div>
  );
}

/**
 * One segmented ring per program: each arc is a phase sized by its share of the work items,
 * filled to what is closed. A ring dark at one end and pale at the other is a program
 * advancing without finishing what it started.
 */
function Rings({ programs, tok, show, hide }: ChartProps) {
  const ps = programs.slice(0, 12);
  const size = 96, cols = Math.min(4, Math.max(2, Math.ceil(Math.sqrt(ps.length))));
  const W = cols * size, H = Math.ceil(ps.length / cols) * (size + 20);
  const arc = (cx: number, cy: number, rad: number, from: number, to: number) => {
    const large = to - from > Math.PI ? 1 : 0;
    return `M ${cx + rad * Math.cos(from)} ${cy + rad * Math.sin(from)} ` +
           `A ${rad} ${rad} 0 ${large} 1 ${cx + rad * Math.cos(to)} ${cy + rad * Math.sin(to)}`;
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {ps.map((p, i) => {
        const cx = (i % cols) * size + size / 2;
        const cy = Math.floor(i / cols) * (size + 20) + size / 2 - 4;
        const rad = size / 2 - 14;
        let acc = 0;
        return (
          <g key={p.id}>
            {p.phases.map((f) => {
              if (!p.items || !f.total) return null;
              const share = f.total / p.items;
              const a0 = acc * 2 * Math.PI - Math.PI / 2;
              const a1 = (acc + share) * 2 * Math.PI - Math.PI / 2;
              acc += share;
              const doneShare = f.total ? f.done / f.total : 0;
              return (
                <g key={f.k} style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => show(e, (
                    <><b>{p.name}</b>
                      <TipRow k="Phase" v={PHASE_LABEL[f.k]} />
                      <TipRow k="Closed" v={`${f.done} of ${f.total}`} />
                    </>))}
                  onMouseLeave={hide}
                >
                  <path d={arc(cx, cy, rad, a0, a1 - 0.02)} fill="none" stroke={tok('--ex-line-soft')} strokeWidth={9} />
                  {doneShare > 0 ? (
                    <path d={arc(cx, cy, rad, a0, a0 + (a1 - a0 - 0.02) * doneShare)} fill="none"
                      stroke={tok(PHASE_TOK[f.k]!)} strokeWidth={9} />
                  ) : null}
                  <path d={arc(cx, cy, rad, a0, a1 - 0.02)} fill="none" stroke="transparent" strokeWidth={13} />
                </g>
              );
            })}
            <text x={cx} y={cy + 2} textAnchor="middle"
              style={{ font: "700 14px 'IBM Plex Mono',monospace", fill: tok('--ex-ink') }}>
              {Math.round(p.progress)}%
            </text>
            <text x={cx} y={cy + 15} textAnchor="middle"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{p.id}</text>
            <text x={cx} y={cy + size / 2 + 6} textAnchor="middle"
              style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{clip(p.name, 14)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Where the budget sits: every program a rectangle sized by planned budget, the paid part
 * saturated. Slice-and-dice rather than squarified — with a dozen programs the aspect ratios
 * stay readable and the order stays meaningful, which squarifying would scramble.
 */
function Treemap({ programs, tok, show, hide }: ChartProps) {
  const ps = programs.filter((p) => p.budget > 0).sort((a, b) => b.budget - a.budget);
  if (!ps.length) return <p className="ex-note">No planned budget is recorded for the selected programs.</p>;
  const W = 560, H = 300;
  const total = sum(ps, (p) => p.budget);
  const cells: { p: Program; x: number; y: number; w: number; h: number }[] = [];
  let items = ps.slice(), x0 = 0, y0 = 0, w = W, h = H;
  while (items.length && w > 1 && h > 1) {
    const horiz = w > h;
    const take = Math.max(1, Math.round(items.length / (items.length > 6 ? 2.6 : 1)));
    const rowItems = items.slice(0, take);
    const rowSum = sum(rowItems, (i) => i.budget);
    const frac = rowSum / sum(items, (i) => i.budget);
    const rw = horiz ? w * frac : w, rh = horiz ? h : h * frac;
    let off = 0;
    rowItems.forEach((p) => {
      const f = p.budget / rowSum;
      const cw = horiz ? rw : rw * f, ch = horiz ? rh * f : rh;
      cells.push({ p, x: x0 + (horiz ? 0 : off), y: y0 + (horiz ? off : 0), w: cw, h: ch });
      off += horiz ? ch : cw;
    });
    if (horiz) { x0 += rw; w -= rw; } else { y0 += rh; h -= rh; }
    items = items.slice(take);
  }
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {cells.map(({ p, x, y, w: cw, h: ch }) => {
          const paid = p.budget ? Math.min(1, p.spent / p.budget) : 0;
          return (
            <g key={p.id} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (
                <><b>{p.name}</b>
                  <TipRow k="Id" v={p.id} />
                  <TipRow k="Planned" v={money(p.budget)} />
                  <TipRow k="Paid" v={money(p.spent)} />
                  <TipRow k="Share of budget" v={pctTxt((p.budget / total) * 100)} />
                </>))}
              onMouseLeave={hide}
            >
              <rect x={x + 1} y={y + 1} width={Math.max(1, cw - 2)} height={Math.max(1, ch - 2)} rx={3}
                fill={tok('--ex-accent')} fillOpacity={0.2} />
              <rect x={x + 1} y={y + 1} width={Math.max(1, cw - 2)} height={Math.max(1, (ch - 2) * paid)} rx={3}
                fill={tok('--ex-accent')} fillOpacity={0.85} />
              {cw > 64 && ch > 26 ? (
                <text x={x + 7} y={y + 15} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink') }}>{p.id}</text>
              ) : null}
              {cw > 64 && ch > 40 ? (
                <text x={x + 7} y={y + 27} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>
                  {clip(p.name, Math.floor(cw / 6))}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="ex-legend">
        <span><i className="ex-sw" style={{ background: 'var(--ex-accent)' }} />Paid</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-accent)', opacity: 0.25 }} />Planned budget</span>
        <span>Rectangle area = planned budget</span>
      </div>
    </>
  );
}

/**
 * A one-line stacked bar with its own legend rows — the rail's workhorse. Zero-value parts
 * are dropped from both the bar and the list, so a card never shows a row that says nothing.
 */
function StackBar({ parts, empty }: { parts: { n: string; v: number; c: string }[]; empty?: string }) {
  const live = parts.filter((p) => p.v > 0);
  const total = sum(parts, (p) => p.v);
  if (!total) return <p className="ex-note" style={{ margin: 0 }}>{empty ?? 'Nothing recorded'}</p>;
  return (
    <>
      <div className="ex-stack">
        {live.map((p) => (
          <i key={p.n} style={{ width: `${(p.v / total) * 100}%`, background: p.c }} title={`${p.n}: ${p.v}`} />
        ))}
      </div>
      <div className="ex-ft" style={{ marginTop: 9, borderTop: 0 }}>
        {live.map((p) => (
          <div className="ex-ft-row" key={p.n}>
            <span><i className="ex-sw" style={{ background: p.c, display: 'inline-block', marginRight: 7 }} />{p.n}</span>
            <b>{p.v}</b>
          </div>
        ))}
      </div>
    </>
  );
}

function Panel({ eyebrow, title, note, children }:
  { eyebrow: string; title: string; note: string; children: ReactNode }) {
  return (
    <section className="ex-panel">
      <header>
        <div className="eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
        <p className="ex-note">{note}</p>
      </header>
      <div className="ex-scroll">{children}</div>
    </section>
  );
}

interface ChartProps {
  programs: Program[];
  tok: (n: string) => string;
  show: (e: { clientX: number; clientY: number }, html: ReactNode) => void;
  hide: () => void;
  stateCol?: (p: Program) => string;
}

/* ── Gantt ──────────────────────────────────────────────────────────────────────────── */

/**
 * A shared time axis: quarter gridlines, quarter labels and the TODAY rule. Both Gantts draw
 * against the same scale, so a bar means the same thing on either of them.
 */
function timeAxis(t0: number, t1: number, x: (t: number) => number, L: number, R: number, W: number) {
  const q: { x: number; label: string }[] = [];
  const d = new Date(t0);
  d.setDate(1);
  d.setMonth(Math.floor(d.getMonth() / 3) * 3);
  while (d.getTime() < t1 && q.length < 40) {
    if (d.getTime() >= t0) {
      q.push({ x: x(d.getTime()), label: `Q${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(2)}` });
    }
    d.setMonth(d.getMonth() + 3);
  }
  const now = x(Date.now());
  return { quarters: q, nowX: now >= L && now <= W - R ? now : null };
}

/**
 * PROGRAM GANTT — one row per program, drawn to the prototype the owner approved.
 *
 * The anatomy, and why each piece is there:
 *   · a two-line label — id and name, then work items and methodology;
 *   · one bar split into the phases that hold work, each phase a PALE TRACK for its span
 *     with a SATURATED FILL over it for the share of that phase's work items closed, so the
 *     bar reads "how long" and "how far" at the same time;
 *   · a HOLLOW DIAMOND on the bar's centre line at each phase end — the phase gate;
 *   · a HORIZONTAL DASHED RULE beneath the bar running to the finalized baseline finish;
 *   · a COPPER TAIL continuing that rule wherever the forecast runs past the baseline.
 *
 * No percentage at the right: the fill already says it, and CLAUDE.md §1.9.3 rule 5 — say it
 * once.
 */
function Gantt({ programs, tok, show, hide }: ChartProps) {
  const dated = programs.filter((p) => p.start && p.end);
  if (!dated.length) {
    return (
      <p className="ex-note">
        No program in this selection has a recorded schedule yet. A program gets one when its
        opportunities are pushed into delivery and its timeline is finalized.
      </p>
    );
  }
  const t0 = Math.min(...dated.map((p) => p.start!.getTime()));
  const t1 = Math.max(...dated.flatMap((p) => [p.end!.getTime(), p.baselineEnd?.getTime() ?? p.end!.getTime()]));
  const span = Math.max(1, t1 - t0);
  const L = 300, R = 24, W = 1020, rowH = 50, T = 44, BAR = 14;
  const H = T + dated.length * rowH + 10;
  const x = (t: number) => L + ((t - t0) / span) * (W - L - R);
  const { quarters, nowX } = timeAxis(t0, t1, x, L, R, W);
  const mono = (w: number, sz: number, fill: string) => ({ font: `${w} ${sz}px 'IBM Plex Mono',monospace`, fill });
  /* Only the phases this selection actually uses appear in the legend. */
  const usedPhases = [...new Set(dated.flatMap((p) => p.phases.filter((f) => f.total > 0).map((f) => f.k)))].sort();

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {quarters.map((q, i) => (
          <g key={i}>
            <line x1={q.x} x2={q.x} y1={T - 12} y2={H - 8} stroke={tok('--ex-line-soft')} strokeWidth={1} />
            <text x={q.x} y={T - 18} textAnchor="middle" style={mono(500, 10.5, tok('--ex-muted'))}>{q.label}</text>
          </g>
        ))}
        {nowX != null ? (
          <>
            <line x1={nowX} x2={nowX} y1={T - 10} y2={H - 8} stroke={tok('--ex-copper')} strokeWidth={1.5} strokeDasharray="5 4" />
            <text x={nowX} y={T - 32} textAnchor="middle" style={mono(600, 10.5, tok('--ex-copper'))}>TODAY</text>
          </>
        ) : null}

        {dated.map((p, r) => {
          const y = T + r * rowH;
          const mid = y + BAR / 2;
          const live = p.phases.filter((f) => f.start && f.end && f.total > 0);
          const baseX = p.baselineEnd ? x(p.baselineEnd.getTime()) : null;
          return (
            <g key={p.id}>
              <text x={0} y={y + 11} style={mono(600, 11.5, tok('--ex-ink-2'))}>{p.id}</text>
              <text x={62} y={y + 11} style={{ font: "500 12.5px 'IBM Plex Sans',sans-serif", fill: tok('--ex-ink') }}>
                {clip(p.name, 26)}
              </text>
              <text x={62} y={y + 25} style={mono(500, 10.5, tok('--ex-muted'))}>
                {p.items} work item{p.items === 1 ? '' : 's'} · {p.methodology}
              </text>

              {/* The programme's whole window, so gaps between phases still read as one bar. */}
              <rect x={x(p.start!.getTime())} y={y} width={Math.max(2, x(p.end!.getTime()) - x(p.start!.getTime()))}
                height={BAR} rx={2} fill={tok('--ex-line-soft')} />

              {live.map((f) => {
                const x0 = x(f.start!.getTime());
                const x1 = x(f.end!.getTime());
                const w = Math.max(2, x1 - x0);
                const share = f.total ? f.done / f.total : 0;
                const col = tok(PHASE_TOK[f.k]!);
                return (
                  <g key={f.k} style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => show(e, (
                      <><b>{p.name}</b>
                        <TipRow k="Phase" v={PHASE_LABEL[f.k]} />
                        <TipRow k="Window" v={`${shortDate(f.start)} → ${shortDate(f.end)}`} />
                        <TipRow k="Work closed" v={`${f.done} of ${f.total}`} />
                        <TipRow k="Blocked" v={f.blocked} />
                      </>))}
                    onMouseLeave={hide}
                  >
                    <rect x={x0} y={y} width={w} height={BAR} rx={2} fill={col} fillOpacity={0.42} />
                    {share > 0 ? (
                      <rect x={x0} y={y} width={Math.max(2, w * share)} height={BAR} rx={2} fill={col} />
                    ) : null}
                    {/* A hairline of the panel's ground between phases. */}
                    <line x1={x0} x2={x0} y1={y} y2={y + BAR} stroke="#fff" strokeWidth={1.5} />
                  </g>
                );
              })}

              {/*
                THE PHASE GATES ARE A SECOND PASS, and have to be.
                Drawn inside the loop, each gate sat under the NEXT phase's band, which
                painted over its right half — every interior diamond rendered as a half
                diamond. Every band is laid down first, then every gate on top.
              */}
              {live.map((f) => {
                const gx = x(f.end!.getTime());
                return (
                  <path key={`gate-${f.k}`} d={`M ${gx} ${mid - 5} l 4.5 5 l -4.5 5 l -4.5 -5 Z`}
                    fill="#fff" stroke={tok('--ex-ink-2')} strokeWidth={1.1} />
                );
              })}

              {/* The finalized baseline, and the slippage past it. */}
              {baseX != null ? (
                <g style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => show(e, (
                    <><b>{p.name}</b>
                      <TipRow k="Baseline finish" v={shortDate(p.baselineEnd)} />
                      <TipRow k="Forecast finish" v={shortDate(p.end)} />
                      <TipRow k="Movement" v={`${p.slipDays > 0 ? '+' : ''}${p.slipDays} days`} />
                    </>))}
                  onMouseLeave={hide}
                >
                  <line x1={x(p.start!.getTime())} x2={baseX} y1={y + BAR + 8} y2={y + BAR + 8}
                    stroke={tok('--ex-ink-2')} strokeWidth={1.1} strokeDasharray="5 4" />
                  {p.slipDays > 0 && p.end ? (
                    <line x1={baseX} x2={x(p.end.getTime())} y1={y + BAR + 8} y2={y + BAR + 8}
                      stroke={tok('--ex-copper')} strokeWidth={3.2} strokeLinecap="round" />
                  ) : null}
                </g>
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="ex-legend">
        {usedPhases.map((k) => (
          <span key={k}><i className="ex-sw" style={{ background: `var(${PHASE_TOK[k]})` }} />{PHASE_SHORT[k]}</span>
        ))}
        <span style={{ color: 'var(--ex-copper)' }}>
          <i className="ex-sw" style={{ background: 'var(--ex-copper)' }} />Slippage past baseline
        </span>
        <span>Fill = work items closed · ◇ phase gate</span>
      </div>
    </>
  );
}

/**
 * PHASE-LEVEL GANTT — one row per phase, grouped under its program.
 *
 * Only phases that HOLD WORK get a row. The first version gave all seven a row whichever way,
 * so a waterfall program drew two empty lanes for Agile and flat delivery and the chart was
 * mostly blank space. Each bar shows the forecast window with the closed share filled, the
 * baseline finish behind it, and its dates and counts at the right where they can be read
 * without hovering.
 */
function PhaseGantt({ programs, tok, show, hide }: ChartProps) {
  const dated = programs.filter((p) => p.start && p.end);
  const rows = dated.flatMap((p) => {
    const live = p.phases.filter((f) => f.start && f.end && f.total > 0);
    return live.map((f, i) => ({ p, f, first: i === 0, count: live.length }));
  });
  if (!rows.length) {
    return (
      <p className="ex-note">
        No phase in this selection has both a schedule and work items against it yet.
      </p>
    );
  }
  const t0 = Math.min(...dated.map((p) => p.start!.getTime()));
  const t1 = Math.max(...dated.flatMap((p) => [p.end!.getTime(), p.baselineEnd?.getTime() ?? p.end!.getTime()]));
  const span = Math.max(1, t1 - t0);
  const L = 250, R = 128, W = 980, rowH = 24, headH = 22, T = 46, groupGap = 12;
  /*
   * A program gets a HEADER ROW of its own before its phases. Putting the program name on
   * the first phase's row collided it with that phase's label — two different things
   * competing for the same 250px.
   */
  let H = T + 14;
  dated.forEach((p) => {
    const n = rows.filter((r) => r.p.id === p.id).length;
    if (n) H += headH + n * rowH + groupGap;
  });
  const x = (t: number) => L + ((t - t0) / span) * (W - L - R);
  const { quarters, nowX } = timeAxis(t0, t1, x, L, R, W);
  const mono = (w: number, sz: number, fill: string) => ({ font: `${w} ${sz}px 'IBM Plex Mono',monospace`, fill });

  let y = T;
  const heads: { p: Program; y: number }[] = [];
  const drawn: { p: Program; f: Program['phases'][number]; y: number }[] = [];
  dated.forEach((p) => {
    const mine = rows.filter((r) => r.p.id === p.id);
    if (!mine.length) return;
    heads.push({ p, y });
    y += headH;
    mine.forEach((r) => { drawn.push({ p, f: r.f, y }); y += rowH; });
    y += groupGap;
  });

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {quarters.map((q, i) => (
          <g key={i}>
            <line x1={q.x} x2={q.x} y1={T - 14} y2={H - 10} stroke={tok('--ex-line-soft')} strokeWidth={1} />
            <text x={q.x + 4} y={T - 18} style={mono(500, 10, tok('--ex-muted'))}>{q.label}</text>
          </g>
        ))}
        {nowX != null ? (
          <>
            <line x1={nowX} x2={nowX} y1={T - 26} y2={H - 10} stroke={tok('--ex-copper')} strokeWidth={1.6} strokeDasharray="4 3" />
            <text x={nowX + 5} y={T - 30} style={mono(600, 10, tok('--ex-copper'))}>TODAY</text>
          </>
        ) : null}

        {heads.map(({ p, y: yy }) => (
          <g key={`h-${p.id}`}>
            <text x={0} y={yy + 13} style={mono(600, 11, tok('--ex-ink-2'))}>{p.id}</text>
            <text x={50} y={yy + 13} style={{ font: "600 12px 'IBM Plex Sans',sans-serif", fill: tok('--ex-ink') }}>
              {clip(p.name, 24)}
            </text>
            {/* Right-ALIGNED, not right-positioned: "Waterfall · on baseline" is wider than the
                gutter and ran off the edge when it started at a fixed x. */}
            <text x={W - 4} y={yy + 13} textAnchor="end" style={mono(500, 9.5, tok('--ex-muted'))}>
              {p.methodology} · {p.slipDays > 0 ? `+${p.slipDays}d slipped`
                : p.slipDays < 0 ? `${Math.abs(p.slipDays)}d recovered` : 'on baseline'}
            </text>
            <line x1={0} x2={W} y1={yy + 18} y2={yy + 18} stroke={tok('--ex-line-soft')} strokeWidth={1} />
          </g>
        ))}

        {drawn.map(({ p, f, y: yy }) => {
          const x0 = x(f.start!.getTime());
          const x1 = x(f.end!.getTime());
          const w = Math.max(3, x1 - x0);
          const share = f.total ? f.done / f.total : 0;
          const col = tok(PHASE_TOK[f.k]!);
          return (
            <g key={`${p.id}-${f.k}`}>
              <text x={18} y={yy + 16} style={{ font: "500 11.5px 'IBM Plex Sans',sans-serif", fill: tok('--ex-ink-2') }}>
                {PHASE_LABEL[f.k]}
              </text>
              <g style={{ cursor: 'pointer' }}
                onMouseMove={(e) => show(e, (
                  <><b>{p.name}</b>
                    <TipRow k="Phase" v={PHASE_LABEL[f.k]} />
                    <TipRow k="Window" v={`${shortDate(f.start)} → ${shortDate(f.end)}`} />
                    <TipRow k="Work closed" v={`${f.done} of ${f.total}`} />
                    <TipRow k="Blocked" v={f.blocked} />
                    <TipRow k="Not started" v={f.notStarted} />
                  </>))}
                onMouseLeave={hide}
              >
                <rect x={x0} y={yy + 5} width={w} height={12} rx={3} fill={col} fillOpacity={0.32} />
                {share > 0 ? (
                  <rect x={x0} y={yy + 5} width={Math.max(2, w * share)} height={12} rx={3} fill={col} />
                ) : null}
                <rect x={x0} y={yy + 2} width={w} height={18} fill="transparent" />
              </g>
              {p.baselineEnd ? (
                <line x1={x(p.baselineEnd.getTime())} x2={x(p.baselineEnd.getTime())} y1={yy + 2} y2={yy + 20}
                  stroke={tok('--ex-ink-2')} strokeWidth={1} strokeDasharray="2 2" />
              ) : null}
              <text x={W - R + 8} y={yy + 16} style={mono(500, 10, tok('--ex-muted'))}>
                {shortDate(f.start)} → {shortDate(f.end)}
              </text>
              <text x={W - 4} y={yy + 16} textAnchor="end" style={mono(700, 10.5, tok('--ex-ink'))}>
                {f.done}/{f.total}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="ex-legend">
        {PHASE_SHORT.map((sh, k) => (
          <span key={sh}><i className="ex-sw" style={{ background: `var(${PHASE_TOK[k]})` }} />{sh}</span>
        ))}
        <span>Solid = work items closed · ┆ baseline finish · phases with no work are not listed</span>
      </div>
    </>
  );
}

function Confidence({ programs, tok, show, hide, stateCol }: ChartProps) {
  const W = 560, H = 330, L = 52, R = 18, T = 22, B = 46;
  const iw = W - L - R, ih = H - T - B;
  const xMax = Math.max(35, ...programs.map((p) => Math.abs(p.slipDays) + 10));
  const x = (v: number) => L + ((Math.max(-xMax, Math.min(xMax, v)) + xMax) / (2 * xMax)) * iw;
  const y = (v: number) => T + (1 - (Math.max(-30, Math.min(30, v)) + 30) / 60) * ih;
  const maxB = Math.max(1, ...programs.map((p) => p.budget));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      <rect x={x(0)} y={y(0)} width={L + iw - x(0)} height={T + ih - y(0)} fill={tok('--ex-crit-soft')} opacity={0.7} />
      {[-30, -15, 0, 15, 30].map((g) => (
        <g key={g}>
          <line x1={L} x2={L + iw} y1={y(g)} y2={y(g)} stroke={tok('--ex-line-soft')} strokeWidth={1} />
          <text x={L - 8} y={y(g) + 3.5} textAnchor="end"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{g > 0 ? `+${g}` : g}pp</text>
        </g>
      ))}
      <line x1={x(0)} x2={x(0)} y1={T} y2={T + ih} stroke={tok('--ex-line')} strokeWidth={1} />
      <line x1={L} x2={L + iw} y1={y(0)} y2={y(0)} stroke={tok('--ex-line')} strokeWidth={1} />
      {[-xMax, 0, xMax].map((v) => (
        <text key={v} x={x(v)} y={H - 26} textAnchor="middle"
          style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>
          {v > 0 ? '+' : ''}{Math.round(v)}d
        </text>
      ))}
      <text x={L + iw / 2} y={H - 9} textAnchor="middle"
        style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>SCHEDULE MOVEMENT →</text>
      <text x={12} y={T + ih / 2} textAnchor="middle" transform={`rotate(-90 12 ${T + ih / 2})`}
        style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>CLOSED vs ELAPSED</text>
      <text x={L + iw - 6} y={T + ih - 7} textAnchor="end"
        style={{ font: "600 9.5px 'IBM Plex Mono',monospace", fill: tok('--ex-muted'), letterSpacing: '.08em' }}>
        LATE AND BEHIND
      </text>
      {programs.map((p) => {
        const idx = p.progress - p.elapsed;
        const r = 9 + Math.sqrt(p.budget / maxB) * 19;
        const col = stateCol!(p);
        return (
          <g key={p.id} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{p.name}</b>
                <TipRow k="Id" v={p.id} />
                <TipRow k="Planned budget" v={money(p.budget)} />
                <TipRow k="Work closed" v={`${p.done} of ${p.items}`} />
                <TipRow k="Time elapsed" v={pctTxt(p.elapsed)} />
                <TipRow k="Schedule" v={`${p.slipDays > 0 ? '+' : ''}${p.slipDays} days`} />
              </>))}
            onMouseLeave={hide}
          >
            <circle cx={x(p.slipDays)} cy={y(idx)} r={r} fill={col} fillOpacity={0.2} stroke={col} strokeWidth={1.6} />
            <text x={x(p.slipDays)} y={y(idx) + 3.5} textAnchor="middle"
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: col }}>{p.id.slice(-3)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Phase bars ─────────────────────────────────────────────────────────────────────── */

function PhaseBars({ programs, tok, show, hide }: ChartProps) {
  const rowH = 34, L = 190, R = 48, W = 560;
  const H = 28 + programs.length * rowH + 8;
  const cellW = (W - L - R) / PHASES.length - 5;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {PHASE_SHORT.map((ph, k) => (
        <text key={ph} x={L + k * (cellW + 6) + cellW / 2} y={14} textAnchor="middle"
          style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{ph.toUpperCase()}</text>
      ))}
      {programs.map((p, r) => {
        const y = 28 + r * rowH;
        return (
          <g key={p.id}>
            <text x={0} y={y + 17} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{p.id}</text>
            <text x={46} y={y + 17} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{clip(p.name, 17)}</text>
            {p.phases.map((f) => {
              const share = f.total ? f.done / f.total : 0;
              const xx = L + f.k * (cellW + 6);
              const fill = share > 0.8 ? tok('--ex-s5') : share > 0.55 ? tok('--ex-s4')
                : share > 0.3 ? tok('--ex-s3') : share > 0.1 ? tok('--ex-s2') : tok('--ex-s1');
              return (
                <g key={f.k} style={{ cursor: 'pointer' }}
                  onMouseMove={(e) => show(e, (
                    <><b>{p.name}</b>
                      <TipRow k="Phase" v={PHASE_LABEL[f.k]} />
                      <TipRow k="Closed" v={`${f.done} of ${f.total}`} />
                      <TipRow k="Blocked" v={f.blocked} />
                    </>))}
                  onMouseLeave={hide}
                >
                  <rect x={xx} y={y + 5} width={cellW} height={16} rx={2} fill={tok('--ex-line-soft')} />
                  <rect x={xx} y={y + 5} width={Math.max(2, cellW * share)} height={16} rx={2} fill={fill} />
                </g>
              );
            })}
            <text x={W - 2} y={y + 17} textAnchor="end"
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{Math.round(p.progress)}%</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Slip ───────────────────────────────────────────────────────────────────────────── */

function Slip({ programs, tok, show, hide }: ChartProps) {
  const ps = programs.slice().sort((a, b) => b.slipDays - a.slipDays);
  const W = 820, rowH = 28, L = 250, R = 56;
  const H = 22 + ps.length * rowH + 16;
  const max = Math.max(20, ...ps.map((p) => Math.abs(p.slipDays))) * 1.2;
  const zero = L + (W - L - R) / 2, scale = (W - L - R) / 2 / max;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      <line x1={zero} x2={zero} y1={14} y2={H - 16} stroke={tok('--ex-line')} strokeWidth={1} />
      <text x={zero} y={9} textAnchor="middle" style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>BASELINE</text>
      {ps.map((p, r) => {
        const y = 22 + r * rowH;
        const w = Math.abs(p.slipDays) * scale;
        const col = p.slipDays > 22 ? tok('--ex-crit') : p.slipDays > 7 ? tok('--ex-warn') : tok('--ex-good');
        return (
          <g key={p.id} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{p.name}</b>
                <TipRow k="Movement" v={`${p.slipDays > 0 ? '+' : ''}${p.slipDays} days`} />
                <TipRow k="Baseline end" v={shortDate(p.baselineEnd)} />
                <TipRow k="Forecast end" v={shortDate(p.end)} />
              </>))}
            onMouseLeave={hide}
          >
            <text x={0} y={y + 14} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{p.id}</text>
            <text x={46} y={y + 14} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{clip(p.name, 24)}</text>
            <rect x={p.slipDays >= 0 ? zero : zero - w} y={y + 3} width={Math.max(2, w)} height={16} rx={2}
              fill={col} fillOpacity={0.85} />
            <text x={p.slipDays >= 0 ? zero + w + 6 : zero - w - 6} y={y + 15}
              textAnchor={p.slipDays >= 0 ? 'start' : 'end'}
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: col }}>
              {p.slipDays > 0 ? '+' : ''}{p.slipDays}d
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Budget ─────────────────────────────────────────────────────────────────────────── */

function BudgetBullets({ programs, tok, show, hide }: ChartProps) {
  const ps = programs.slice().sort((a, b) => b.budget - a.budget);
  const maxB = Math.max(1, ...ps.map((p) => p.budget));
  if (!ps.some((p) => p.budget > 0 || p.spent > 0)) {
    return <p className="ex-note">No budget or payment has been recorded for the selected programs.</p>;
  }
  return (
    <>
      <div className="ex-bullets">
        {ps.map((p) => {
          const used = p.budget ? (p.spent / p.budget) * 100 : 0;
          const cls = used > 100 ? 'crit' : used > 90 ? 'warn' : 'good';
          return (
            <div key={p.id}>
              <div className="ex-bullet-hd">
                <span style={{ font: "500 11px/1 'IBM Plex Mono',monospace", color: 'var(--ex-muted)' }}>{p.id}</span>
                <span className="nm">{p.name}</span>
                <span className="fig">{money(p.spent)} of {money(p.budget)}</span>
                <span className={`ex-chip ${cls}`}>{p.budget ? pctTxt(used) : '—'} used</span>
              </div>
              <div
                className="ex-track"
                style={{ width: `${Math.max(24, (p.budget / maxB) * 100)}%` }}
                onMouseMove={(e) => show(e, (
                  <><b>{p.name}</b>
                    <TipRow k="Planned" v={money(p.budget)} />
                    <TipRow k="Paid" v={money(p.spent)} />
                    <TipRow k="Remaining" v={money(Math.max(0, p.budget - p.spent))} />
                  </>))}
                onMouseLeave={hide}
              >
                <div style={{ position: 'absolute', inset: '0 auto 0 0', borderRadius: 3,
                  width: `${Math.min(100, p.budget ? (p.spent / p.budget) * 100 : 0)}%`,
                  background: used > 100 ? tok('--ex-crit') : tok('--ex-accent') }} />
                <div style={{ position: 'absolute', top: -4, bottom: -4, left: '100%', width: 2, background: tok('--ex-copper') }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="ex-legend">
        <span><i className="ex-sw" style={{ background: 'var(--ex-accent)' }} />Paid</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-line-soft)' }} />Planned budget</span>
        <span style={{ color: 'var(--ex-copper)' }}>
          <i className="ex-sw" style={{ background: 'var(--ex-copper)', width: 3, height: 13 }} />Plan limit
        </span>
      </div>
    </>
  );
}

function Burn({ payments, budget, tok, show, hide }:
  { payments: PaymentRow[]; budget: number } & Pick<ChartProps, 'tok' | 'show' | 'hide'>) {
  const dated = payments.map((p) => ({ d: parseDate(p.payment_date), a: Number(p.amount || 0) }))
    .filter((p): p is { d: Date; a: number } => !!p.d)
    .sort((a, b) => a.d.getTime() - b.d.getTime());
  if (dated.length < 2) return <p className="ex-note">Not enough payments recorded to draw a burn curve.</p>;
  const W = 560, H = 270, L = 58, R = 16, T = 16, B = 40;
  const iw = W - L - R, ih = H - T - B;
  const t0 = dated[0]!.d.getTime(), t1 = dated[dated.length - 1]!.d.getTime();
  const span = Math.max(1, t1 - t0);
  let acc = 0;
  const pts = dated.map((p) => { acc += p.a; return { x: L + ((p.d.getTime() - t0) / span) * iw, v: acc, d: p.d }; });
  const top = Math.max(budget, acc) * 1.08 || 1;
  const y = (v: number) => T + (1 - v / top) * ih;
  const path = pts.map((p, i) => `${i ? 'L' : 'M'} ${p.x.toFixed(1)} ${y(p.v).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {[0, 1, 2, 3, 4].map((q) => (
        <g key={q}>
          <line x1={L} x2={L + iw} y1={y((top * q) / 4)} y2={y((top * q) / 4)} stroke={tok('--ex-line-soft')} strokeWidth={1} />
          <text x={L - 7} y={y((top * q) / 4) + 3.5} textAnchor="end"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{money((top * q) / 4)}</text>
        </g>
      ))}
      {budget > 0 ? (
        <>
          <line x1={L} x2={L + iw} y1={y(budget)} y2={y(budget)} stroke={tok('--ex-ink-2')} strokeWidth={1.2} strokeDasharray="6 3" />
          <text x={L + 5} y={y(budget) - 5} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>
            PLANNED {money(budget)}
          </text>
        </>
      ) : null}
      <path d={`${path} L ${pts[pts.length - 1]!.x} ${y(0)} L ${pts[0]!.x} ${y(0)} Z`} fill={tok('--ex-accent')} fillOpacity={0.14} />
      <path d={path} fill="none" stroke={tok('--ex-accent')} strokeWidth={2.5} />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={y(p.v)} r={3} fill={tok('--ex-accent')} style={{ cursor: 'pointer' }}
          onMouseMove={(e) => show(e, (
            <><b>{shortDate(p.d)}</b><TipRow k="Cumulative paid" v={money(p.v)} /></>))}
          onMouseLeave={hide} />
      ))}
      <text x={L} y={H - 12} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{shortDate(dated[0]!.d)}</text>
      <text x={L + iw} y={H - 12} textAnchor="end"
        style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{shortDate(dated[dated.length - 1]!.d)}</text>
    </svg>
  );
}

function EarnedValue({ programs, tok, show, hide, stateCol }: ChartProps) {
  const usable = programs.filter((p) => p.budget > 0 && p.spent > 0 && p.elapsed > 0);
  if (!usable.length) return <p className="ex-note">Earned value needs a planned budget and recorded payments.</p>;
  const W = 560, H = 300, L = 48, R = 16, T = 16, B = 42;
  const iw = W - L - R, ih = H - T - B, lo = 0.7, hi = 1.3;
  const x = (v: number) => L + ((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * iw;
  const y = (v: number) => T + (1 - (Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * ih;
  const maxB = Math.max(1, ...usable.map((p) => p.budget));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      <rect x={L} y={y(1)} width={x(1) - L} height={T + ih - y(1)} fill={tok('--ex-crit-soft')} opacity={0.7} />
      {[0.7, 0.85, 1, 1.15, 1.3].map((v) => (
        <g key={v}>
          <line x1={L} x2={L + iw} y1={y(v)} y2={y(v)} stroke={tok('--ex-line-soft')} strokeWidth={1} />
          <text x={L - 7} y={y(v) + 3.5} textAnchor="end"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{v.toFixed(2)}</text>
          <text x={x(v)} y={H - 24} textAnchor="middle"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{v.toFixed(2)}</text>
        </g>
      ))}
      <line x1={x(1)} x2={x(1)} y1={T} y2={T + ih} stroke={tok('--ex-line')} strokeWidth={1} />
      <line x1={L} x2={L + iw} y1={y(1)} y2={y(1)} stroke={tok('--ex-line')} strokeWidth={1} />
      <text x={L + iw / 2} y={H - 8} textAnchor="middle"
        style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>COST PERFORMANCE (CPI) →</text>
      <text x={12} y={T + ih / 2} textAnchor="middle" transform={`rotate(-90 12 ${T + ih / 2})`}
        style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>SCHEDULE (SPI)</text>
      {usable.map((p) => {
        const ev = (p.budget * p.progress) / 100;
        const cpi = ev / Math.max(1, p.spent);
        const spi = p.progress / Math.max(1, p.elapsed);
        const r = 9 + Math.sqrt(p.budget / maxB) * 17;
        const col = stateCol!(p);
        return (
          <g key={p.id} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{p.name}</b>
                <TipRow k="CPI" v={cpi.toFixed(2)} />
                <TipRow k="SPI" v={spi.toFixed(2)} />
                <TipRow k="Value earned" v={money(ev)} />
                <TipRow k="Cost incurred" v={money(p.spent)} />
              </>))}
            onMouseLeave={hide}
          >
            <circle cx={x(cpi)} cy={y(spi)} r={r} fill={col} fillOpacity={0.2} stroke={col} strokeWidth={1.6} />
            <text x={x(cpi)} y={y(spi) + 3.5} textAnchor="middle"
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: col }}>{p.id.slice(-3)}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Resources ──────────────────────────────────────────────────────────────────────── */

function UtilHeat({ programs, tok, show, hide }: ChartProps) {
  const any = programs.some((p) => p.ftePlan > 0 || p.fteActual > 0);
  if (!any) return <p className="ex-note">No planned or captured effort has been recorded for these programs.</p>;
  const W = 700, L = 250, R = 16, rowH = 32, T = 24;
  const H = T + programs.length * rowH + 12;
  const cellW = (W - L - R) / PHASES.length - 4;
  const col = (u: number | null) =>
    u == null ? tok('--ex-line-soft')
    : u > 112 ? tok('--ex-crit') : u > 103 ? tok('--ex-warn')
    : u >= 88 ? tok('--ex-good') : u >= 72 ? tok('--ex-s2') : tok('--ex-warn-soft');
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {PHASE_SHORT.map((ph, k) => (
          <text key={ph} x={L + k * (cellW + 4) + cellW / 2} y={14} textAnchor="middle"
            style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{ph.toUpperCase()}</text>
        ))}
        {programs.map((p, r) => {
          const y = T + r * rowH;
          return (
            <g key={p.id}>
              <text x={0} y={y + 18} style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{p.id}</text>
              <text x={46} y={y + 18} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{clip(p.name, 24)}</text>
              {p.phases.map((f) => {
                const u = f.ftePlan > 0 ? (f.fteActual / f.ftePlan) * 100 : null;
                const xx = L + f.k * (cellW + 4);
                return (
                  <g key={f.k} style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => show(e, (
                      <><b>{p.name}</b>
                        <TipRow k="Phase" v={PHASE_LABEL[f.k]} />
                        <TipRow k="Planned" v={`${f.ftePlan.toFixed(1)} FTE`} />
                        <TipRow k="Captured" v={`${f.fteActual.toFixed(1)} FTE`} />
                        <TipRow k="Utilisation" v={u == null ? 'no plan' : pctTxt(u)} />
                      </>))}
                    onMouseLeave={hide}
                  >
                    <rect x={xx} y={y + 5} width={cellW} height={rowH - 12} rx={2} fill={col(u)} />
                    {u != null && cellW > 30 ? (
                      <text x={xx + cellW / 2} y={y + 18} textAnchor="middle"
                        style={{ font: "600 9.5px 'IBM Plex Mono',monospace",
                          fill: u > 103 || u < 72 ? '#fff' : tok('--ex-ink') }}>{Math.round(u)}</text>
                    ) : null}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div className="ex-legend">
        <span><i className="ex-sw" style={{ background: 'var(--ex-warn-soft)' }} />Under 72% — not started</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-s2)' }} />72–88%</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-good)' }} />88–103% on plan</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-warn)' }} />103–112% hot</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-crit)' }} />Over 112%</span>
        <span><i className="ex-sw" style={{ background: 'var(--ex-line-soft)' }} />No plan recorded</span>
      </div>
    </>
  );
}

function PeopleBars({ rows, tok, show, hide }:
  { rows: PeopleRow[] } & Pick<ChartProps, 'tok' | 'show' | 'hide'>) {
  const byOwner: Record<string, { open: number; total: number }> = {};
  rows.forEach((r) => {
    const o = (byOwner[r.owner] ??= { open: 0, total: 0 });
    o.open += Number(r.open_items || 0); o.total += Number(r.items || 0);
  });
  const list = Object.entries(byOwner).map(([n, v]) => ({ n, ...v }))
    .sort((a, b) => b.open - a.open).slice(0, 12);
  if (!list.length) return <p className="ex-note">No work item in the selection has a named owner.</p>;
  const W = 560, rowH = 26, L = 170, R = 60;
  const H = 14 + list.length * rowH;
  const max = Math.max(1, ...list.map((d) => d.total));
  const x = (v: number) => L + (v / max) * (W - L - R);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {list.map((d, r) => {
        const y = 10 + r * rowH;
        const col = d.open > 8 ? tok('--ex-crit') : d.open > 4 ? tok('--ex-warn') : tok('--ex-accent');
        return (
          <g key={d.n} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{d.n}</b>
                <TipRow k="Open work items" v={d.open} />
                <TipRow k="Total assigned" v={d.total} />
              </>))}
            onMouseLeave={hide}
          >
            <text x={0} y={y + 13} style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{clip(d.n, 22)}</text>
            <rect x={L} y={y + 3} width={Math.max(2, x(d.total) - L)} height={13} rx={2} fill={tok('--ex-line-soft')} />
            <rect x={L} y={y + 3} width={Math.max(2, x(d.open) - L)} height={13} rx={2} fill={col} />
            <text x={x(d.total) + 6} y={y + 14}
              style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{d.open}/{d.total}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Risk + actions ─────────────────────────────────────────────────────────────────── */

function RiskHeat({ risks, byId, tok, show, hide }:
  { risks: RiskRow[]; byId: Record<string, Program> } & Pick<ChartProps, 'tok' | 'show' | 'hide'>) {
  if (!risks.length) return <p className="ex-note">No risk is recorded against the selected programs.</p>;
  const cellOf = (sev: string, st: string) =>
    risks.filter((r) => r.severity === sev && (RISK_STATE.includes(r.status) ? r.status : 'Identified') === st).length;
  const max = Math.max(1, ...RISK_SEV.flatMap((s) => RISK_STATE.map((t) => cellOf(s, t))));
  const ramp = ['--ex-s1', '--ex-s2', '--ex-s3', '--ex-s4', '--ex-s5'];
  const order: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
  return (
    <>
      <div className="ex-heat">
        <div />
        {RISK_STATE.map((s) => <div className="ex-heat-h" key={s}>{s}</div>)}
        {RISK_SEV.map((sev) => (
          <div key={sev} style={{ display: 'contents' }}>
            <div className="ex-heat-r">{sev}</div>
            {RISK_STATE.map((st) => {
              const n = cellOf(sev, st);
              const k = n === 0 ? -1 : Math.min(4, Math.round((n / max) * 4));
              const alert = sev === 'Critical' && st === 'Identified' && n > 0;
              const style: CSSProperties = {
                background: n === 0 ? 'var(--ex-surface-2)' : `var(${ramp[k]})`,
                color: n === 0 ? 'var(--ex-muted)' : k >= 3 ? '#fff' : 'var(--ex-ink)',
                ...(alert ? { boxShadow: 'inset 0 0 0 2px var(--ex-copper)' } : {}),
              };
              return (
                <div className="ex-cell" key={st} style={style}
                  onMouseMove={(e) => show(e, (
                    <><b>{sev} · {st}</b>
                      <TipRow k="Open risks" v={n} />
                      {alert ? <TipRow k="" v="No mitigation recorded" /> : null}
                    </>))}
                  onMouseLeave={hide}
                >{n || '·'}</div>
              );
            })}
          </div>
        ))}
      </div>
      <ul className="ex-risks">
        {risks.slice().sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9)).slice(0, 6).map((r) => (
          <li key={r.id}>
            <span className="ex-risk-bar" style={{
              background: r.severity === 'Critical' ? tok('--ex-crit')
                : r.severity === 'High' ? tok('--ex-warn') : tok('--ex-s3'),
            }} />
            <span>
              <span className="ex-risk-t">{r.title}</span>
              <span className="ex-risk-m">
                {r.mitigation ? `${r.mitigation} · ` : ''}{r.owner || 'unowned'} ·{' '}
                {byId[r.delivery_project_id]?.id ?? r.delivery_project_id}
              </span>
            </span>
            <span className={`ex-chip ${r.severity === 'Critical' ? 'crit' : r.severity === 'High' ? 'warn' : 'flat'}`}>
              {r.status || 'Identified'}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function ActionAge({ actions, tok, show, hide }:
  { actions: ActionRow[] } & Pick<ChartProps, 'tok' | 'show' | 'hide'>) {
  if (!actions.length) return <p className="ex-note">No open action is recorded against the selected programs.</p>;
  const W = 560, H = 230, L = 40, R = 14, T = 16, B = 48;
  const iw = W - L - R, ih = H - T - B;
  const counts = AGE_BUCKETS.map(([lo, hi]) =>
    PRIORITIES.map(([pr]) => actions.filter((a) => a.age_days >= lo && a.age_days <= hi && a.priority === pr).length));
  const max = Math.max(1, ...counts.map((c) => c.reduce((a, b) => a + b, 0)));
  const bw = iw / AGE_BUCKETS.length - 20;
  const stale = actions.filter((a) => a.age_days > 30 && (a.priority === 'Critical' || a.priority === 'High'))
    .sort((a, b) => b.age_days - a.age_days).slice(0, 6);
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {[0, 1, 2, 3, 4].map((q) => {
          const v = Math.round((max * q) / 4), yy = T + ih - (v / max) * ih;
          return (
            <g key={q}>
              <line x1={L} x2={L + iw} y1={yy} y2={yy} stroke={tok('--ex-line-soft')} strokeWidth={1} />
              <text x={L - 7} y={yy + 3.5} textAnchor="end"
                style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{v}</text>
            </g>
          );
        })}
        {AGE_BUCKETS.map(([, , lbl], bi) => {
          const xx = L + bi * (iw / AGE_BUCKETS.length) + 10;
          let acc = 0;
          const tot = counts[bi]!.reduce((a, b) => a + b, 0);
          return (
            <g key={lbl}>
              {PRIORITIES.map(([pr, token], pi) => {
                const n = counts[bi]![pi]!;
                if (!n) return null;
                const h = (n / max) * ih, yy = T + ih - acc - h;
                acc += h;
                return (
                  <rect key={pr} x={xx} y={yy} width={bw} height={h} rx={2} fill={tok(token)} style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => show(e, (
                      <><b>{lbl}</b><TipRow k="Priority" v={pr} /><TipRow k="Open actions" v={n} /></>))}
                    onMouseLeave={hide} />
                );
              })}
              <text x={xx + bw / 2} y={T + ih + 15} textAnchor="middle"
                style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok('--ex-muted') }}>{lbl}</text>
              {tot ? (
                <text x={xx + bw / 2} y={T + ih - acc - 5} textAnchor="middle"
                  style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>{tot}</text>
              ) : null}
            </g>
          );
        })}
        <text x={L + iw / 2} y={H - 10} textAnchor="middle"
          style={{ font: "600 10px 'IBM Plex Mono',monospace", fill: tok('--ex-ink-2') }}>AGE SINCE RAISED</text>
      </svg>
      <div className="ex-legend">
        {PRIORITIES.map(([pr, token]) => (
          <span key={pr}><i className="ex-sw" style={{ background: `var(${token})` }} />{pr}</span>
        ))}
      </div>
      <table style={{ marginTop: 12 }}>
        <thead>
          <tr><th>Open longest, high priority</th><th>Owner</th><th>Program</th><th style={{ textAlign: 'right' }}>Age</th></tr>
        </thead>
        <tbody>
          {stale.length ? stale.map((a) => (
            <tr key={a.id}>
              <td><span className="ex-wi-id">A-{a.id}</span><span className="ex-wi-name">{a.title}</span></td>
              <td>{a.owner || '—'}</td>
              <td style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>{a.delivery_project_id}</td>
              <td className="num" style={{ color: a.age_days > 60 ? tok('--ex-crit') : tok('--ex-warn') }}>{a.age_days}d</td>
            </tr>
          )) : (
            <tr><td colSpan={4} style={{ color: 'var(--ex-muted)' }}>No high-priority action older than 30 days.</td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}

function OverdueTable({ rows, tok }: { rows: OverdueRow[]; tok: (n: string) => string }) {
  if (!rows.length) {
    return <p className="ex-note">Nothing overdue in the selected programs.</p>;
  }
  const chip = (s: string) =>
    s === 'Blocked' ? 'crit' : s === 'Not Started' ? 'warn' : 'info';
  return (
    <table>
      <thead>
        <tr>
          <th>Work item</th><th>Program</th><th>Phase</th><th>Stream</th><th>Owner</th>
          <th style={{ textAlign: 'right' }}>Due</th><th style={{ textAlign: 'right' }}>Late</th>
          <th style={{ textAlign: 'right' }}>Done</th><th>State</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 15).map((r) => (
          <tr key={r.id}>
            <td>
              <span className="ex-wi-id">{r.work_item_type || 'Work item'}</span>
              <span className="ex-wi-name">{r.name}</span>
            </td>
            <td style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11 }}>{r.delivery_project_id}</td>
            <td>{PHASE_SHORT[PHASES.indexOf(r.phase as never)] ?? r.phase}</td>
            <td>{r.stream || '—'}</td>
            <td>{r.owner || '—'}</td>
            <td className="num">{shortDate(parseDate(r.delivered_by_date))}</td>
            <td className="num" style={{ color: r.days_late > 30 ? tok('--ex-crit') : tok('--ex-warn') }}>{r.days_late}d</td>
            <td className="num">{r.progress}%</td>
            <td><span className={`ex-chip ${chip(r.status)}`}>{r.status || '—'}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
